import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_BASE as DEFAULT_API_BASE } from "../config/apiConfig";
import { formatRunId } from "./Runs";
import PytestLogInspector from "../components/PytestLogInspector";

function ExecutionTriggerForm({ API_BASE, setPage, onRunLaunched }) {
  const [trProj, setTrProj] = useState("Qintra");
  const [trEnv, setTrEnv] = useState("QA");
  const [trTestCase, setTrTestCase] = useState("ALL");
  const [trDataset, setTrDataset] = useState("LoginUsers_100 (100 Rows)");
  const [trReqId, setTrReqId] = useState("REQ-0001 - Auth Security");
  const [trRunTitle, setTrRunTitle] = useState(`RUN_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}_01`);
  // eslint-disable-next-line no-unused-vars
  const [trStrategy, setTrStrategy] = useState("parallel");
  const [trWorkers, setTrWorkers] = useState(10);
  const [trBrowsers, setTrBrowsers] = useState({ chromium: true, firefox: false, webkit: false });
  const [trEmail, setTrEmail] = useState("qa_lead@qintra.ai");
  const [trEmailTrigger, setTrEmailTrigger] = useState("on_failure");
  const [trSelfHeal, setTrSelfHeal] = useState(true);
  const [trCaptureArtifacts, setTrCaptureArtifacts] = useState(true);
  const [trLaunching, setTrLaunching] = useState(false);
  const [trMessage, setTrMessage] = useState("");

  const [projectList, setProjectList] = useState(["Qintra", "CRM Farm", "E-Commerce Suite", "Pegatto", "Testing"]);
  const [testCasesList, setTestCasesList] = useState([]);
  const [datasetsList, setDatasetsList] = useState([]);

  useEffect(() => {
    const api = API_BASE || DEFAULT_API_BASE;
    axios.get(`${api}/projects-list`).then(res => {
      if (Array.isArray(res.data) && res.data.length > 0) setProjectList(res.data);
    }).catch(() => { });

    axios.get(`${api}/tdm/datasets?project=${trProj}`).then(res => {
      if (res.data?.datasets) {
        setDatasetsList(res.data.datasets.map(d => `${d.name} (${d.rows || 100} Rows)`));
      }
    }).catch(() => { });

    axios.get(`${api}/test-cases`).then(res => {
      if (res.data && res.data[trProj]) {
        setTestCasesList(res.data[trProj]);
      }
    }).catch(() => { });
  }, [trProj, API_BASE]);

  const handleLaunchRun = async (e) => {
    e.preventDefault();
    setTrLaunching(true);
    setTrMessage("");

    const selectedBrowsers = Object.keys(trBrowsers).filter(b => trBrowsers[b]);
    const runId = trRunTitle || `RUN_${Date.now()}`;
    const api = API_BASE || DEFAULT_API_BASE;

    try {
      await axios.post(`${api}/run-combined`, {
        project: trProj,
        test_case: trTestCase,
        dataset: trDataset,
        requirement_id: trReqId,
        run_id: runId,
        environment: trEnv,
        browsers: selectedBrowsers.length > 0 ? selectedBrowsers : ["chromium"],
        cc_email: trEmail,
        email_trigger: trEmailTrigger,
        parallel_workers: trWorkers,
        strategy: trStrategy,
        enable_healing: trSelfHeal,
        capture_artifacts: trCaptureArtifacts
      });

      setTrLaunching(false);
      setTrMessage(` Successfully triggered execution "${runId}"! ${trWorkers} worker threads active for ${trProj}.`);

      if (onRunLaunched) {
        onRunLaunched({
          runId,
          project: trProj,
          testCase: trTestCase,
          browsers: selectedBrowsers.length > 0 ? selectedBrowsers : ["chromium"],
          estimatedDuration: 45,
          startTime: Date.now(),
          status: "running",
          batchIndex: 1,
          batchTotal: trWorkers
        });
      }
    } catch (err) {
      setTrLaunching(false);
      setTrMessage(` Launch triggered for "${runId}"! Initializing workers...`);
    }
  };

  return (
    <div style={{ backgroundColor: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "16px", padding: "28px", boxShadow: "0 4px 24px rgba(0,0,0,0.04)", textAlign: "left", marginBottom: "24px" }}>
      {/* Header */}
      <div style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "16px", marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "24px" }}></span>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.3px" }}>
              New Test Run Execution Triggering Form
            </h2>
            <span style={{ backgroundColor: "#fff7ed", color: "#FD6101", fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "12px", textTransform: "uppercase" }}>
              Multi-Threaded Runner
            </span>
          </div>
          <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>
            Configure target project, test datasets, business requirements, email notifications, and concurrency settings to launch a suite execution.
          </p>
        </div>

        <button onClick={() => setPage && setPage("runs")} style={{ padding: "8px 16px", backgroundColor: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "8px", fontWeight: "700", fontSize: "12.5px", cursor: "pointer", color: "#334155" }}>
           View Past Runs History
        </button>
      </div>

      {trMessage && (
        <div style={{ padding: "14px 18px", backgroundColor: "#ecfdf5", borderLeft: "4px solid #10b981", borderRadius: "8px", color: "#065f46", fontSize: "13.5px", fontWeight: "600", marginBottom: "24px" }}>
          {trMessage}
        </div>
      )}

      <form onSubmit={handleLaunchRun}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>

          {/* SECTION 1: Target Scope */}
          <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "14px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
              <span></span> 1. Target Scope & Dataset
            </h3>

            {/* Target Project */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>TARGET PROJECT</label>
              <select
                value={trProj}
                onChange={(e) => setTrProj(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", fontWeight: "600" }}
              >
                {projectList.map(p => <option key={p} value={p}> {p}</option>)}
              </select>
            </div>

            {/* Target Test Case / Suite */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>TEST CASE / SUITE</label>
              <select
                value={trTestCase}
                onChange={(e) => setTrTestCase(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", fontWeight: "600" }}
              >
                <option value="ALL"> ALL Test Cases in Suite ({testCasesList.length || 3} Cases)</option>
                {testCasesList.map(tc => (
                  <option key={tc.filename} value={tc.name}>🧪 {tc.name}</option>
                ))}
              </select>
            </div>

            {/* TDM Dataset */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>ATTACH TDM DATASET</label>
              <select
                value={trDataset}
                onChange={(e) => setTrDataset(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", fontWeight: "600" }}
              >
                <option value="LoginUsers_100 (100 Rows)"> LoginUsers_100 (100 Login Records)</option>
                {datasetsList.map(d => <option key={d} value={d}> {d}</option>)}
                <option value="NONE">None (Inline Hardcoded Values)</option>
              </select>
            </div>

            {/* Target Environment */}
            <div>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>ENVIRONMENT</label>
              <div style={{ display: "flex", gap: "8px" }}>
                {["DEV", "QA", "UAT", "PROD"].map(env => (
                  <button
                    key={env}
                    type="button"
                    onClick={() => setTrEnv(env)}
                    style={{
                      flex: 1,
                      padding: "8px",
                      fontSize: "12px",
                      fontWeight: "700",
                      borderRadius: "6px",
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: trEnv === env ? (env === "PROD" ? "#ef4444" : "#0f172a") : "#ffffff",
                      color: trEnv === env ? "#ffffff" : "#475569",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                    }}
                  >
                    {env}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* SECTION 2: Concurrency & Browsers */}
          <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "14px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
              <span></span> 2. Concurrency & Browsers
            </h3>

            {/* Run Identifier */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>RUN TITLE / IDENTIFIER</label>
              <input
                type="text"
                value={trRunTitle}
                onChange={(e) => setTrRunTitle(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", fontFamily: "monospace" }}
              />
            </div>

            {/* Parallel Worker Threads */}
            <div style={{ marginBottom: "14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569" }}>PARALLEL WORKER THREADS</label>
                <span style={{ fontSize: "12px", fontWeight: "800", color: "#FD6101", backgroundColor: "#fff7ed", padding: "2px 8px", borderRadius: "10px" }}>{trWorkers} Workers</span>
              </div>
              <input
                type="range"
                min="1"
                max="20"
                value={trWorkers}
                onChange={(e) => setTrWorkers(Number(e.target.value))}
                style={{ width: "100%", accentColor: "#FD6101" }}
              />
              <span style={{ fontSize: "11px", color: "#64748b" }}>Speed up 100 logins by running multi-threaded parallel browser instances</span>
            </div>

            {/* Browsers Selection */}
            <div>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>TARGET BROWSERS</label>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {[
                  { key: "chromium", label: "Chromium (Chrome/Edge)", icon: "" },
                  { key: "firefox", label: "Firefox", icon: "🦊" },
                  { key: "webkit", label: "WebKit (Safari)", icon: "🧭" }
                ].map(b => (
                  <label key={b.key} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", fontWeight: "600", cursor: "pointer", color: "#334155" }}>
                    <input
                      type="checkbox"
                      checked={trBrowsers[b.key]}
                      onChange={(e) => setTrBrowsers({ ...trBrowsers, [b.key]: e.target.checked })}
                      style={{ accentColor: "#FD6101" }}
                    />
                    <span>{b.icon} {b.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* SECTION 3: Mail & AI Settings */}
          <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "14px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
              <span>📧</span> 3. Mail Sending & Business Mapping
            </h3>

            {/* Business Requirement */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>BUSINESS REQUIREMENT</label>
              <select
                value={trReqId}
                onChange={(e) => setTrReqId(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", fontWeight: "600" }}
              >
                <option value="REQ-0001 - Auth Security"> REQ-0001 - Authentication & SSO Security</option>
                <option value="REQ-0002 - Payment Processing"> REQ-0002 - Payment & Invoice Processing</option>
                <option value="REQ-0003 - EHR Patient Records Privacy"> REQ-0003 - EHR Patient Records Privacy</option>
                <option value="NONE">None</option>
              </select>
            </div>

            {/* Email Recipients */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>MAIL RECIPIENTS (SEPARATE BY COMMA)</label>
              <input
                type="email"
                placeholder="qa_lead@qintra.ai, devops@company.com"
                value={trEmail}
                onChange={(e) => setTrEmail(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
              />
            </div>

            {/* Email Trigger Condition */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "6px" }}>EMAIL NOTIFICATION TRIGGER</label>
              <select
                value={trEmailTrigger}
                onChange={(e) => setTrEmailTrigger(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", fontSize: "13px", borderRadius: "6px", border: "1px solid #cbd5e1", fontWeight: "600" }}
              >
                <option value="on_failure">! Send Only On Failure</option>
                <option value="always">📩 Always Send (Pass & Fail Reports)</option>
                <option value="never">🚫 Never Send Email Alerts</option>
              </select>
            </div>

            {/* AI Healing & Artifacts Toggles */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", fontWeight: "600", color: "#1e293b", cursor: "pointer" }}>
                <input type="checkbox" checked={trSelfHeal} onChange={(e) => setTrSelfHeal(e.target.checked)} style={{ accentColor: "#FD6101" }} />
                <span> Enable AI Vision Self-Healing Selectors</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", fontWeight: "600", color: "#1e293b", cursor: "pointer" }}>
                <input type="checkbox" checked={trCaptureArtifacts} onChange={(e) => setTrCaptureArtifacts(e.target.checked)} style={{ accentColor: "#FD6101" }} />
                <span>📹 Capture Live Video Recordings & Screenshots</span>
              </label>
            </div>
          </div>
        </div>

        {/* Submit Execution Button */}
        <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end" }}>
          <button
            type="submit"
            disabled={trLaunching}
            style={{
              padding: "14px 32px",
              background: "#FD6101",
              color: "#ffffff",
              borderRadius: "10px",
              border: "none",
              fontWeight: "800",
              fontSize: "15px",
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(253, 97, 1, 0.35)",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              transition: "background 0.2s"
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#e05300"}
            onMouseLeave={e => e.currentTarget.style.background = "#FD6101"}>
              transition: "all 0.15s ease"
            }}
          >
            <span>{trLaunching ? " Initializing Execution Workers..." : " Execute Test Suite Now"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default function LiveExecutionPanel({
  currentLiveRun,          // { runId, project, testCase, browsers, estimatedDuration, startTime, status, batchIndex, batchTotal }
  activeRunStages,         // { initialize: { status, duration }, execute, a11y, heal, report, alert }
  selectedRunLogContent,   // Full streamed text log content
  currentRunningStep,      // Active scenario step currently running
  onCancelRun,             // Cancel button callback
  API_BASE,
  setPage,
  onRunLaunched
}) {
  const [selectedBrowser, setSelectedBrowser] = useState(
    currentLiveRun?.browsers?.[0] || "chromium"
  );

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [screenshotTimestamp, setScreenshotTimestamp] = useState(Date.now());
  const logTerminalEndRef = useRef(null);

  const [logViewMode, setLogViewMode] = useState("structured"); // "structured" | "raw"

  // Synchronize default browser selection when run changes
  useEffect(() => {
    if (currentLiveRun?.browsers?.length > 0) {
      setSelectedBrowser(currentLiveRun.browsers[0]);
    }
  }, [currentLiveRun]);

  // Actual Time taken counter
  useEffect(() => {
    if (!currentLiveRun || currentLiveRun.status !== "running") return;

    const elapsedStart = Math.floor((Date.now() - currentLiveRun.startTime) / 1000);
    setElapsedSeconds(elapsedStart > 0 ? elapsedStart : 0);

    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentLiveRun]);

  // Live screenshot refresh logic
  useEffect(() => {
    if (!currentLiveRun || currentLiveRun.status !== "running") return;

    const interval = setInterval(() => {
      setScreenshotTimestamp(Date.now());
    }, 1500);

    return () => clearInterval(interval);
  }, [currentLiveRun]);

  useEffect(() => {
    setScreenshotTimestamp(Date.now());
  }, [currentRunningStep]);

  useEffect(() => {
    if (logTerminalEndRef.current) {
      logTerminalEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedRunLogContent]);

  if (!currentLiveRun) {
    return (
      <div style={{ padding: "24px" }}>
        <ExecutionTriggerForm API_BASE={API_BASE} setPage={setPage} onRunLaunched={onRunLaunched} />
      </div>
    );
  }

  const { runId, project, testCase, browsers, estimatedDuration, status, batchIndex, batchTotal } = currentLiveRun;
  const isFinished = status === "completed" || status === "failed" || status === "cancelled";

  // Progress Bar Percentage
  const progressPercent = isFinished
    ? 100
    : Math.min(Math.round((elapsedSeconds / (estimatedDuration || 45)) * 100), 99);

  // Live Screenshot URL
  const browserSuffix = browsers?.length > 0 ? `_${selectedBrowser}` : "_chromium";
  const liveScreenshotUrl = `${API_BASE}/reports/regression/run_${runId}${browserSuffix}/screenshots/live.png?t=${screenshotTimestamp}`;

  return (
    <div style={{ padding: "24px", color: "#1e293b", fontFamily: "'Inter', sans-serif" }}>

      {/* 1. TOP SECTION: Details of the Run */}
      <div className="panel-card" style={{ padding: "24px", marginBottom: "24px", background: "white", borderRadius: "16px", border: "1.5px solid #ede9fe", boxShadow: "0 4px 20px rgba(124,58,237,0.04)" }}>

        {/* Header Metadata */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
          <div style={{ textAlign: "left" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h2 style={{ fontSize: "22px", fontWeight: "800", color: "#FD6101", margin: 0 }}>
                Live Test Execution
              </h2>
              {batchTotal > 1 && (
                <span style={{ background: "#ede9fe", color: "#FD6101", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "700" }}>
                  Test {batchIndex} of {batchTotal}
                </span>
              )}
            </div>
            <p style={{ color: "#64748b", fontSize: "14px", marginTop: "4px", marginBottom: 0 }}>
              Monitor live console logs, screenshots, and assertions in real-time.
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => setPage("runs")}
              className="btn-secondary"
              style={{ padding: "8px 16px", fontSize: "14px", fontWeight: "600", borderRadius: "8px", border: "1px solid #cbd5e1" }}
            >
              View Past Runs
            </button>
            {status === "running" && (
              <button
                onClick={onCancelRun}
                className="btn-danger"
                style={{ background: "#ef4444", color: "white", border: "none", padding: "8px 16px", fontSize: "14px", fontWeight: "600", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span></span> Stop Run
              </button>
            )}
          </div>
        </div>

        {/* Metadata Details Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", padding: "16px", background: "#f8fafc", borderRadius: "12px", border: "1px solid #cbd5e1", marginBottom: "20px" }}>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>Run Identifier</div>
            <div style={{ fontSize: "15px", fontWeight: "800", color: "#334155", marginTop: "4px" }}>{formatRunId(runId)}</div>
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>Project / Suite</div>
            <div style={{ fontSize: "15px", fontWeight: "800", color: "#334155", marginTop: "4px" }}>{project}</div>
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>Target Test Script</div>
            <div style={{ fontSize: "15px", fontWeight: "800", color: "#334155", marginTop: "4px", wordBreak: "break-all" }}>{testCase || "Real-Time Generated Flow"}</div>
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>Status</div>
            <div style={{ marginTop: "4px" }}>
              {status === "running" ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f5f3ff", color: "#FD6101", padding: "4px 10px", borderRadius: "12px", fontSize: "13px", fontWeight: "700", border: "1px solid #ddd6fe" }}>
                  <span className="live-pulse-dot" style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#FD6101", display: "inline-block" }}></span> Running
                </span>
              ) : status === "completed" ? (
                <span style={{ background: "#ecfdf5", color: "#10b981", padding: "4px 10px", borderRadius: "12px", fontSize: "13px", fontWeight: "700", border: "1px solid #a7f3d0" }}>
                  ✓ Passed
                </span>
              ) : status === "failed" ? (
                <span style={{ background: "#fef2f2", color: "#ef4444", padding: "4px 10px", borderRadius: "12px", fontSize: "13px", fontWeight: "700", border: "1px solid #fecaca" }}>
                  ✗ Failed
                </span>
              ) : (
                <span style={{ background: "#f1f5f9", color: "#64748b", padding: "4px 10px", borderRadius: "12px", fontSize: "13px", fontWeight: "700", border: "1px solid #cbd5e1" }}>
                   Cancelled
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Timers & Real-Time Estimation Progress */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", color: "#475569", fontWeight: "600" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span></span> Elapsed: <strong style={{ color: "#0f172a", fontSize: "14px" }}>{elapsedSeconds}s</strong>
              {!isFinished && <span style={{ color: "#94a3b8", fontWeight: "normal" }}>(Estimated: {estimatedDuration || 45}s)</span>}
            </span>
            <span>{isFinished ? "100% Complete" : `Progress: ${progressPercent}%`}</span>
          </div>

          {/* Progress track */}
          <div style={{ width: "100%", height: "8px", background: "#e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
            <div
              style={{
                width: `${progressPercent}%`,
                height: "100%",
                background: status === "failed" ? "#ef4444" : "#FD6101",
                borderRadius: "10px",
                transition: "width 0.4s ease"
              }}
            />
          </div>
        </div>
      </div>

      {/* 2. NEXT STAGE VIEW: Sequential Execution Timeline */}
      <div className="panel-card" style={{ padding: "20px", marginBottom: "24px", background: "white", borderRadius: "16px", border: "1.5px solid #ede9fe", boxShadow: "0 4px 20px rgba(124,58,237,0.04)" }}>
        <h4 style={{ fontSize: "14px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", margin: "0 0 16px 0", letterSpacing: "0.5px", textAlign: "left" }}>
          Execution Lifecycle Stages
        </h4>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", position: "relative" }}>

          {/* Timeline Connector Line */}
          <div style={{ position: "absolute", top: "18px", left: "20px", right: "20px", height: "3px", background: "#e2e8f0", zIndex: 1 }} />

          {/* Lifecycle Steps */}
          {[
            { id: "initialize", label: "Initialization", desc: "Setting up drivers & env" },
            { id: "execute", label: "Test Execution", desc: "Running playwright assertions" },
            { id: "a11y", label: "A11y Check", desc: "Verifying compliance score" },
            { id: "heal", label: "Auto-Healing", desc: "Correcting failed selectors" },
            { id: "report", label: "Report Gen", desc: "Packaging test results" },
            { id: "alert", label: "Notification", desc: "Dispatching reports via SMTP" }
          ].map((stage, idx) => {
            const val = activeRunStages?.[stage.id]?.status || "pending";

            // Define colors
            let borderBg = "#cbd5e1";
            let textBg = "#94a3b8";
            let color = "#64748b";
            let dotContent = idx + 1;
            let animateClass = "";

            if (val === "active") {
              borderBg = "#a78bfa";
              textBg = "#FD6101";
              color = "#FD6101";
              animateClass = "stage-pulsing-node";
            } else if (val === "completed") {
              borderBg = "#10b981";
              textBg = "#10b981";
              color = "#047857";
              dotContent = "✓";
            } else if (val === "failed") {
              borderBg = "#ef4444";
              textBg = "#ef4444";
              color = "#b91c1c";
              dotContent = "✗";
            } else if (val === "skipped") {
              borderBg = "#e2e8f0";
              textBg = "#cbd5e1";
              color = "#94a3b8";
              dotContent = "->";
            }

            return (
              <div
                key={stage.id}
                className={animateClass}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, minWidth: "110px", zIndex: 2 }}
              >
                {/* Node circle */}
                <div style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: val === "active" ? "#ffffff" : textBg,
                  border: `3px solid ${borderBg}`,
                  color: val === "active" ? "#FD6101" : "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "800",
                  fontSize: "14px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                }}>
                  {dotContent}
                </div>

                {/* Node labels */}
                <div style={{ marginTop: "10px", textAlign: "center" }}>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: color }}>{stage.label}</div>
                  <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>{stage.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Browser Tab Selector for Parallel Runs */}
      {browsers?.length > 1 && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px", borderBottom: "1px solid #e2e8f0", paddingBottom: "10px" }}>
          {browsers.map(b => (
            <button
              key={b}
              onClick={() => setSelectedBrowser(b)}
              style={{
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: "700",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                background: selectedBrowser === b ? "#FD6101" : "#f1f5f9",
                color: selectedBrowser === b ? "white" : "#475569",
                boxShadow: selectedBrowser === b ? "0 2px 6px rgba(124,58,237,0.3)" : "none"
              }}
            >
              {b.toUpperCase()} Run
            </button>
          ))}
        </div>
      )}

      {/* 3. SPLIT DETAILS VIEW: Live Log Terminal vs Live Screen Shot & Steps */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", minHeight: "520px" }}>

        {/* LEFT PANEL: Live Screenshot and Running Step */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* Active Step Status */}
          <div className="panel-card" style={{ padding: "16px 20px", background: "#fdf4ff", border: "1.5px solid #f5d0fe", borderRadius: "12px", display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "24px" }}></div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#d946ef", letterSpacing: "0.5px" }}>Active Scenario Step</div>
              <div style={{ fontSize: "15px", fontWeight: "800", color: "#701a75", marginTop: "4px" }}>
                {currentRunningStep || "Preparing web driver environment..."}
              </div>
            </div>
          </div>

          {/* Live Browser Viewport display */}
          <div className="panel-card" style={{ padding: "16px", background: "white", borderRadius: "16px", border: "1.5px solid #ede9fe", flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <h4 style={{ fontSize: "14px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", margin: 0, letterSpacing: "0.5px", textAlign: "left" }}>
                Live Browser Viewport
              </h4>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#FD6101", backgroundColor: "#f5f3ff", padding: "2px 8px", borderRadius: "10px" }}>
                1920x1080 ({selectedBrowser.toUpperCase()})
              </span>
            </div>

            {/* Browser Window Chrome & Address Bar */}
            <div style={{ backgroundColor: "#1e293b", borderRadius: "10px 10px 0 0", padding: "8px 12px", display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ display: "flex", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#ef4444", display: "inline-block" }}></span>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#f59e0b", display: "inline-block" }}></span>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#10b981", display: "inline-block" }}></span>
              </div>
              <div style={{ flex: 1, backgroundColor: "#0f172a", borderRadius: "6px", padding: "4px 10px", fontSize: "11.5px", color: "#94a3b8", fontFamily: "monospace", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ color: "#10b981" }}></span> https://demo.myapplication.com/checkout
              </div>
            </div>

            <div style={{ flex: 1, background: "#f8fafc", border: "1px solid #cbd5e1", borderTop: "none", borderRadius: "0 0 10px 10px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: "340px" }}>
              {status === "running" ? (
                <img
                  src={liveScreenshotUrl}
                  alt="Live Browser Viewport"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                  }}
                  onLoad={(e) => {
                    e.target.style.display = "block";
                    if (e.target.nextSibling) e.target.nextSibling.style.display = "none";
                  }}
                />
              ) : (
                <div style={{ display: "flex", width: "100%", height: "100%", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                  <div style={{ fontSize: "40px", marginBottom: "8px" }}>🎬</div>
                  <div style={{ fontWeight: "700" }}>Execution Concluded</div>
                  <div style={{ fontSize: "12px", marginTop: "4px" }}>Select a past run to watch full recorded video.</div>
                </div>
              )}

              {/* Loading Placeholder */}
              <div
                style={{
                  display: "flex",
                  position: "absolute",
                  top: 0, left: 0, right: 0, bottom: 0,
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#94a3b8",
                  background: "#f8fafc",
                  width: "100%", height: "100%"
                }}
              >
                <div className="stage-pulsing-node" style={{ width: "30px", height: "30px", border: "3px solid #FD6101", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: "12px" }}></div>
                <div style={{ fontWeight: "600", fontSize: "14px" }}>Synchronizing Live Screen viewport...</div>
                <div style={{ fontSize: "12px", marginTop: "4px" }}>Takes screenshot on page interactions</div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Standard Output Logs & Dual-View Inspector */}
        <div className="panel-card" style={{ padding: "16px", background: "white", borderRadius: "16px", border: "1.5px solid #ede9fe", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <h4 style={{ fontSize: "14px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", margin: 0, letterSpacing: "0.5px", textAlign: "left" }}>
                Standard Output Logs
              </h4>
              <div style={{ display: "flex", backgroundColor: "#f1f5f9", borderRadius: "6px", padding: "2px" }}>
                <button
                  type="button"
                  onClick={() => setLogViewMode("structured")}
                  style={{ padding: "4px 10px", fontSize: "11px", fontWeight: "700", border: "none", borderRadius: "4px", backgroundColor: logViewMode === "structured" ? "#FD6101" : "transparent", color: logViewMode === "structured" ? "#ffffff" : "#64748b", cursor: "pointer" }}
                >
                   Structured
                </button>
                <button
                  type="button"
                  onClick={() => setLogViewMode("raw")}
                  style={{ padding: "4px 10px", fontSize: "11px", fontWeight: "700", border: "none", borderRadius: "4px", backgroundColor: logViewMode === "raw" ? "#FD6101" : "transparent", color: logViewMode === "raw" ? "#ffffff" : "#64748b", cursor: "pointer" }}
                >
                   Raw Terminal
                </button>
              </div>
            </div>
            <span style={{ fontSize: "11px", fontWeight: "700", color: "#10b981", background: "#ecfdf5", padding: "2px 8px", borderRadius: "20px", display: "flex", alignItems: "center", gap: "4px" }}>
              <span className="live-pulse-dot" style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#10b981" }}></span> Live Log Tailing
            </span>
          </div>

          {logViewMode === "structured" ? (
            <div style={{ flex: 1, overflowY: "auto", minHeight: "440px" }}>
              <PytestLogInspector rawLog={selectedRunLogContent || "Initializing Pytest runner log stream...\nRunning test cases..."} />
            </div>
          ) : (
            <div style={{
              flex: 1,
              background: "#0f172a",
              borderRadius: "12px",
              padding: "16px",
              overflowY: "auto",
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: "12.5px",
              color: "#e2e8f0",
              textAlign: "left",
              boxShadow: "inset 0 2px 10px rgba(0,0,0,0.4)",
              maxHeight: "500px",
              minHeight: "440px"
            }}>
              {selectedRunLogContent ? (
                selectedRunLogContent.split("\n").map((line, idx) => {
                  if (line.includes("[LIVE_STEP]")) return null;
                  let color = "#e2e8f0";
                  if (line.includes("passed") || line.includes("SUCCESS")) color = "#10b981";
                  else if (line.includes("failed") || line.includes("FAILED") || line.includes("ERROR") || line.includes("Traceback")) color = "#ef4444";
                  else if (line.includes("WARNING")) color = "#f59e0b";
                  else if (line.startsWith("===") || line.startsWith("---")) color = "#a78bfa";

                  return (
                    <div key={idx} style={{ color: color, marginBottom: "4px", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {line}
                    </div>
                  );
                })
              ) : (
                <div style={{ color: "#64748b", fontStyle: "italic" }}>
                  Waiting for runner logs to stream...
                </div>
              )}
              <div ref={logTerminalEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Trigger Form for New Runs */}
      <div style={{ marginTop: "30px" }}>
        <ExecutionTriggerForm API_BASE={API_BASE} setPage={setPage} onRunLaunched={onRunLaunched} />
      </div>

      {/* Styles insertion directly to the DOM */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .stage-pulsing-node {
          position: relative;
        }
        .stage-pulsing-node::after {
          content: '';
          position: absolute;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: 2px solid #c084fc;
          animation: pulseNode 2.2s infinite ease-in-out;
          top: -4px;
          left: -4px;
          z-index: -1;
        }
        @keyframes pulseNode {
          0% {
            transform: scale(0.8);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.25);
            opacity: 0.35;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        .live-pulse-dot {
          animation: blinkDot 1.5s infinite;
        }
        @keyframes blinkDot {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

